// Real-mode (Firestore, Mumbai) versions of the clinical record functions.
// Visits (FHIR Encounter), prescriptions (MedicationRequest), templates and clinic settings.
// The database rules (firestore.rules) are the real guard; these mirror them so the UI fails early.
import {
  addDoc, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where,
} from 'firebase/firestore';
import type { Coding, Encounter, MedicationRequest, Mother, PrescriptionTemplate, RxContent, VitalLimit } from '../types';
import { db } from '../lib/firebase';
import { DEFAULT_LIMITS, STD_UNITS, type DefaultUnits } from './codes';
import { addAudit, type Actor } from './auditService';
import { getUserProfile } from './userService';
import { loadClinic } from './store';
import { scope } from './scope';

const fdb = () => db!;
const col = (fid: string, name: string) => collection(fdb(), 'facilities', fid, name);
const ref = (fid: string, name: string, id: string) => doc(fdb(), 'facilities', fid, name, id);
// Firestore refuses `undefined` values; drop them.
const clean = <T>(v: T): T => JSON.parse(JSON.stringify(v));
const patient = (id: string) => `Patient ${id}`;
const uid = () => scope().uid ?? '';

// The database stores the signer's user id (checked by the rules); screens show the name.
type Stored<T> = T & { signedByName?: string };
const named = <T extends { signedBy?: string }>(x: Stored<T>): T => ({ ...x, signedBy: x.signedByName ?? x.signedBy });

// ---------- visits ----------

export async function getVisits(motherId: string): Promise<Encounter[]> {
  const fid = scope().facilityId;
  // Staff may only see visits the doctor has not signed yet, so they must ask for exactly those.
  const q = scope().role === 'staff'
    ? query(col(fid, 'encounters'), where('subjectId', '==', motherId), where('status', '==', 'in-progress'))
    : query(col(fid, 'encounters'), where('subjectId', '==', motherId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => named<Encounter>({ ...(d.data() as Stored<Encounter>), id: d.id })).sort((a, b) => b.date.localeCompare(a.date));
}

export async function getVisit(id: string): Promise<Encounter | undefined> {
  try {
    const snap = await getDoc(ref(scope().facilityId, 'encounters', id));
    return snap.exists() ? named<Encounter>({ ...(snap.data() as Stored<Encounter>), id: snap.id }) : undefined;
  } catch {
    return undefined; // e.g. staff opening a signed visit
  }
}

export async function saveVisit(input: Encounter, actor: Actor | undefined, sign: boolean): Promise<Encounter> {
  const doctor = actor?.role === 'doctor';
  if (sign && !doctor) throw new Error('Only a doctor can sign a visit');
  const fid = input.facilityId;
  const r = ref(fid, 'encounters', input.id);
  const prev = await getVisit(input.id);
  let saved: Encounter;
  if (doctor) {
    const data: Stored<Encounter> = sign
      ? { ...input, status: 'finished', signedBy: uid(), signedByName: actor?.name, signedAt: new Date().toISOString() }
      : { ...input, status: 'in-progress', signedBy: undefined, signedAt: undefined };
    await setDoc(r, clean(data));
    saved = named(data);
  } else if (prev) {
    // Staff and facility admin: vitals only.
    const patch = { vitals: input.vitals, recordedBy: input.recordedBy, recordedAt: input.recordedAt ?? new Date().toISOString(), status: 'in-progress' as const };
    await updateDoc(r, clean(patch));
    saved = { ...prev, ...patch };
  } else {
    const { resourceType, id, subjectId, date, vitals, recordedBy, recordedAt } = input;
    const data = { resourceType, id, facilityId: fid, subjectId, date, status: 'in-progress' as const, vitals, recordedBy, recordedAt };
    await setDoc(r, clean(data));
    saved = { ...input, ...data, symptoms: [], symptomNote: '', findings: '', ultrasound: '', conditions: [], testsOrdered: '' };
  }
  addAudit(actor, sign ? 'visit.signed' : 'visit.saved', patient(saved.subjectId), fid, saved.id);
  return saved;
}

/** Mothers with a visit recorded today (vitals taken or signed): they came in. */
export async function getVisitedToday(facilityId: string, todayIso: string): Promise<string[]> {
  // Reception may only read visits still waiting for the doctor (firestore.rules), so its query asks for exactly those.
  const q = scope().role === 'staff'
    ? query(col(facilityId, 'encounters'), where('date', '==', todayIso), where('status', '==', 'in-progress'))
    : query(col(facilityId, 'encounters'), where('date', '==', todayIso));
  const snap = await getDocs(q);
  return Array.from(new Set(snap.docs.map((d) => String(d.get('subjectId')))));
}

export async function getAwaitingDoctor(facilityId: string): Promise<{ visit: Encounter; mother: Mother }[]> {
  const [snap, clinic] = await Promise.all([
    getDocs(query(col(facilityId, 'encounters'), where('status', '==', 'in-progress'))),
    loadClinic(facilityId),
  ]);
  const byId = new Map(clinic.mothers.map((m) => [m.id, m]));
  return snap.docs
    .map((d) => ({ ...(d.data() as Encounter), id: d.id }))
    .filter((v) => byId.has(v.subjectId))
    .sort((a, b) => (a.recordedAt ?? a.date).localeCompare(b.recordedAt ?? b.date))
    .map((visit) => ({ visit, mother: byId.get(visit.subjectId)! }));
}

// ---------- prescriptions ----------

export async function getPrescriptions(motherId: string): Promise<MedicationRequest[]> {
  const fid = scope().facilityId;
  try {
    const [rx, visits] = await Promise.all([
      getDocs(query(col(fid, 'medicationRequests'), where('subjectId', '==', motherId))),
      getVisits(motherId),
    ]);
    // Only prescriptions attached to a signed visit count as part of the patient record.
    const signed = visits.filter((v) => v.status === 'finished');
    return rx.docs
      .map((d) => named<MedicationRequest>({ ...(d.data() as Stored<MedicationRequest>), id: d.id }))
      .filter((r) => signed.some((v) => v.medicationRequestId === r.id || v.id === r.encounterId))
      .sort((a, b) => b.signedAt.localeCompare(a.signedAt));
  } catch {
    return []; // staff cannot see prescriptions
  }
}

export async function getPrescription(id: string): Promise<MedicationRequest | undefined> {
  if (!id) return undefined;
  try {
    const snap = await getDoc(ref(scope().facilityId, 'medicationRequests', id));
    return snap.exists() ? named<MedicationRequest>({ ...(snap.data() as Stored<MedicationRequest>), id: snap.id }) : undefined;
  } catch {
    return undefined;
  }
}

export async function signPrescription(facilityId: string, subjectId: string, rx: RxContent, templateId: string | undefined, actor: Actor | undefined, diagnoses: Coding[]): Promise<MedicationRequest> {
  if (actor?.role !== 'doctor') throw new Error('Only a doctor can approve a prescription');
  const me = await getUserProfile(uid());
  const d = me.doctor;
  if (!d?.regNo || !d.council) throw new Error('NO_REG');
  if (!rx.medicines.length || rx.medicines.some((m) => !m.name.trim() || !m.dose.trim())) throw new Error('Every medicine needs a generic name and dose');
  const r = doc(col(facilityId, 'medicationRequests'));
  const data: Stored<MedicationRequest> = {
    resourceType: 'MedicationRequest', id: r.id, facilityId, subjectId, templateId, status: 'active',
    signedBy: uid(), signedByName: me.name, signedAt: new Date().toISOString(),
    signer: { name: me.name, ...d }, diagnoses, medicines: rx.medicines, advice: rx.advice, followUp: rx.followUp,
  };
  await setDoc(r, clean(data));
  addAudit(actor, 'prescription.signed', patient(subjectId), facilityId, r.id);
  return named(data);
}

// ---------- prescription templates (doctors write, doctors + admin read) ----------

export async function getTemplates(facilityId: string): Promise<PrescriptionTemplate[]> {
  try {
    const snap = await getDocs(col(facilityId, 'rxTemplates'));
    return snap.docs.map((d) => ({ ...(d.data() as PrescriptionTemplate), id: d.id })).sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
}

export async function saveTemplate(tpl: PrescriptionTemplate, actor?: Actor): Promise<PrescriptionTemplate> {
  const r = tpl.id ? ref(tpl.facilityId, 'rxTemplates', tpl.id) : doc(col(tpl.facilityId, 'rxTemplates'));
  const saved = { ...tpl, id: r.id, updatedAt: new Date().toISOString() };
  await setDoc(r, clean(saved));
  addAudit(actor, 'template.saved', saved.name, saved.facilityId);
  return saved;
}

export async function duplicateTemplate(id: string, actor?: Actor): Promise<void> {
  const fid = scope().facilityId;
  const snap = await getDoc(ref(fid, 'rxTemplates', id));
  if (!snap.exists()) return;
  const t = snap.data() as PrescriptionTemplate;
  const r = await addDoc(col(fid, 'rxTemplates'), clean({ ...t, name: `${t.name} (copy)`, archived: false, updatedAt: new Date().toISOString() }));
  await updateDoc(r, { id: r.id });
  addAudit(actor, 'template.duplicated', t.name, fid);
}

export async function setTemplateArchived(id: string, archived: boolean, actor?: Actor): Promise<void> {
  const fid = scope().facilityId;
  await updateDoc(ref(fid, 'rxTemplates', id), { archived, updatedAt: new Date().toISOString() });
  addAudit(actor, archived ? 'template.archived' : 'template.restored', id, fid);
}

// ---------- clinic settings: units and vital-sign safety ranges ----------

async function setting<T>(fid: string, id: string, key: string, fallback: T): Promise<T> {
  try {
    const snap = await getDoc(ref(fid, 'settings', id));
    return snap.exists() ? { ...fallback, ...(snap.data()[key] as T) } : fallback;
  } catch {
    return fallback;
  }
}

export const getDefaultUnits = (fid: string): Promise<DefaultUnits> => setting(fid, 'units', 'units', { ...STD_UNITS });

export async function saveDefaultUnits(fid: string, units: DefaultUnits, actor?: Actor): Promise<void> {
  await setDoc(ref(fid, 'settings', 'units'), { units: clean(units), updatedAt: new Date().toISOString(), updatedBy: uid() });
  addAudit(actor, 'units.saved', fid, fid, Object.values(units).join(', '));
}

export const getVitalLimits = (fid: string): Promise<Record<string, VitalLimit>> =>
  setting(fid, 'vitalLimits', 'limits', JSON.parse(JSON.stringify(DEFAULT_LIMITS)) as Record<string, VitalLimit>);

export async function saveVitalLimits(fid: string, limits: Record<string, VitalLimit>, actor?: Actor): Promise<void> {
  // merge: keeps `sources` (which master version each programme's ranges came from).
  await setDoc(ref(fid, 'settings', 'vitalLimits'), { limits: clean(limits), updatedAt: new Date().toISOString(), updatedBy: uid() }, { merge: true });
  addAudit(actor, 'limits.saved', fid, fid);
}
